/**
 * Accept-invitation landing — `/invite/:token`.
 */
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { getApiClient } from "../services/apiClientProvider";

type State =
	| { kind: "loading" }
	| { kind: "ready" }
	| { kind: "submitting" }
	| { kind: "success"; organizationName: string }
	| { kind: "error"; message: string };

export function AcceptInvitationPage() {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const { status, actions } = useAuth();
	const [state, setState] = useState<State>({ kind: "loading" });

	useEffect(() => {
		if (status === "loading") return;
		if (!token) {
			setState({ kind: "error", message: "Missing invitation token." });
			return;
		}
		if (status !== "authenticated") {
			const next = encodeURIComponent(`/invite/${token}`);
			navigate(`/login?next=${next}`, { replace: true });
			return;
		}
		setState({ kind: "ready" });
	}, [status, token, navigate]);

	const handleAccept = async () => {
		if (!token) return;
		setState({ kind: "submitting" });
		try {
			const res = await getApiClient().acceptInvitation({ token });
			await actions.refresh();
			setState({ kind: "success", organizationName: res.organization.name });
		} catch (err) {
			setState({
				kind: "error",
				message: err instanceof Error ? err.message : String(err),
			});
		}
	};

	return (
		<Box sx={{ maxWidth: 560, mx: "auto", mt: { xs: 2, md: 4 } }}>
			<GlassSurface
				intensity="strong"
				depth="raised"
				sx={{ p: { xs: 2.5, sm: 3 } }}
			>
				<Stack spacing={2}>
					<Typography
						variant="overline"
						sx={{
							color: "workspace.green",
							letterSpacing: "0.1em",
							fontWeight: 700,
						}}
					>
						Organization invitation
					</Typography>
					<Typography component="h1" variant="h5">
						Join organization
					</Typography>
					{state.kind === "loading" && (
						<CircularProgress size={24} aria-label="Loading invitation" />
					)}
					{state.kind === "ready" && (
						<>
							<Typography sx={{ color: "workspace.textSecondary" }}>
								You were invited to join an organization on FlahaSOIL. Confirm
								below to accept.
							</Typography>
							<Button
								variant="contained"
								onClick={handleAccept}
								sx={{ alignSelf: "flex-start" }}
							>
								Accept invitation
							</Button>
						</>
					)}
					{state.kind === "submitting" && (
						<Stack direction="row" spacing={1} alignItems="center">
							<CircularProgress size={18} />
							<Typography>Accepting…</Typography>
						</Stack>
					)}
					{state.kind === "success" && (
						<>
							<Alert severity="success">
								You are now a member of{" "}
								<strong>{state.organizationName}</strong>.
							</Alert>
							<Button
								component={RouterLink}
								to="/dashboard" variant="contained"
								sx={{ alignSelf: "flex-start" }}
							>
								Continue to dashboard
							</Button>
						</>
					)}
					{state.kind === "error" && (
						<>
							<Alert severity="error">{state.message}</Alert>
							<Button
								component={RouterLink}
								to="/dashboard" sx={{ alignSelf: "flex-start" }}
							>
								Back to dashboard
							</Button>
						</>
					)}
				</Stack>
			</GlassSurface>
		</Box>
	);
}
